import React, { useEffect, useState } from 'react';
import { Button, Card, DatePicker, Image, Input, InputNumber, Modal, Popconfirm, Space, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, PlayCircleOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { del, get } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import HlsMedia from '../../components/HlsMedia';

const { RangePicker } = DatePicker;

interface UserVoice {
  id: string;
  user_id: number;
  username: string;
  title: string;
  script: string;
  voice_name: string;
  voice_id: string;
  audio_url: string;
  audio_format: string;
  cover_url: string;
  bgm_title: string;
  mood: string;
  prompt: string;
  created_at: string;
}

interface UserVoiceListResponse {
  total: number;
  page: number;
  page_size: number;
  items: UserVoice[];
}

const UserVoicesPage: React.FC = () => {
  const [items, setItems] = useState<UserVoice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  // 过滤条件
  const [userId, setUserId] = useState<number | null>(null);
  const [keyword, setKeyword] = useState('');
  const [range, setRange] = useState<[string, string] | null>(null);
  const [playing, setPlaying] = useState<UserVoice | null>(null);

  const fetchItems = async (toPage = page, size = pageSize) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(toPage));
      params.set('page_size', String(size));
      if (userId != null) params.set('user_id', String(userId));
      if (keyword.trim()) params.set('keyword', keyword.trim());
      if (range) {
        params.set('start', range[0]);
        params.set('end', range[1]);
      }
      const data = await get<UserVoiceListResponse>(`${API_ENDPOINTS.storage.userVoices}?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || toPage);
      setPageSize(data.page_size || size);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载用户生成内容失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => fetchItems(1, pageSize);

  const handleReset = () => {
    setUserId(null);
    setKeyword('');
    setRange(null);
    setTimeout(() => fetchItems(1, pageSize), 0);
  };

  const handleDelete = async (record: UserVoice) => {
    try {
      await del(API_ENDPOINTS.storage.userVoiceDetail(record.id));
      message.success('已删除');
      // 删除后若当前页空了则回退一页
      const nextTotal = total - 1;
      const maxPage = Math.max(1, Math.ceil(nextTotal / pageSize));
      fetchItems(Math.min(page, maxPage), pageSize);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '删除失败');
    }
  };

  const columns: ColumnsType<UserVoice> = [
    {
      title: '用户ID',
      dataIndex: 'user_id',
      width: 110,
      render: (v) => <Tag color="blue" style={{ fontSize: 13, fontWeight: 600 }}>{v}</Tag>,
    },
    {
      title: '用户',
      width: 150,
      render: (_, r) => <strong>{r.username || '未知用户'}</strong>,
    },
    {
      title: '封面',
      dataIndex: 'cover_url',
      width: 104,
      render: (url: string, record) => url ? (
        <Image
          src={url}
          alt={`${record.title || '生成音频'}封面`}
          width={80}
          height={45}
          style={{ objectFit: 'cover', borderRadius: 6 }}
        />
      ) : <span style={{ color: '#aaa' }}>待生成</span>,
    },
    {
      title: '标题 / 文案',
      render: (_, r) => (
        <div>
          <strong>{r.title}</strong>
          <div style={{ color: '#999', fontSize: 12, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.script || r.prompt || '-'}
          </div>
        </div>
      ),
    },
    { title: '心情', dataIndex: 'mood', width: 100, render: (v) => (v ? <Tag color="magenta">{v}</Tag> : '-') },
    { title: '声线', dataIndex: 'voice_name', width: 100, render: (v) => v || '-' },
    { title: '背景乐', dataIndex: 'bgm_title', width: 120, render: (v) => v || '-' },
    { title: '创建时间', dataIndex: 'created_at', width: 170, render: (v) => v || '-' },
    {
      title: '操作',
      width: 160,
      fixed: 'right',
      render: (_, r) => (
        <Space>
          <Button
            type="link"
            icon={<PlayCircleOutlined />}
            disabled={!r.audio_url}
            onClick={() => setPlaying(r)}
          >
            播放
          </Button>
          <Popconfirm title="确定删除这条生成内容？" onConfirm={() => handleDelete(r)} okText="删除" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="用户生成内容"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => fetchItems(page, pageSize)}>
          刷新
        </Button>
      }
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <InputNumber
          placeholder="用户 ID"
          value={userId ?? undefined}
          onChange={(v) => setUserId(typeof v === 'number' ? v : null)}
          min={0}
          style={{ width: 120 }}
        />
        <Input
          placeholder="标题/文案/提示词"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
          style={{ width: 220 }}
        />
        <RangePicker
          onChange={(_, strs) => setRange(strs[0] && strs[1] ? [strs[0], strs[1]] : null)}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
        <Button onClick={handleReset}>重置</Button>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        scroll={{ x: 1100 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, s) => fetchItems(p, s),
        }}
      />

      <Modal open={!!playing} footer={null} onCancel={() => setPlaying(null)} title={playing?.title} destroyOnClose>
        {playing && (
          <div>
            {playing.cover_url ? (
              <Image src={playing.cover_url} alt={`${playing.title}封面`} style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 8 }} />
            ) : null}
            <p style={{ color: '#666', whiteSpace: 'pre-wrap' }}>{playing.script || playing.prompt || ''}</p>
            {playing.audio_url ? (
              <HlsMedia src={playing.audio_url} controls autoPlay style={{ width: '100%' }} />
            ) : (
              <p>该记录没有可播放音频</p>
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default UserVoicesPage;
